import { useEffect, useState, useCallback } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowUpRight, Star, X, ChevronLeft, ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { legacyProjectRedirect } from "../lib/redirects";
import Seo, { breadcrumbSchema } from "../components/Seo";
import { Reveal, Overline, FrameReveal } from "../components/Reveal";
import { BeforeAfter } from "../components/BeforeAfter";
import RelatedInsights from "../components/RelatedInsights";
import CtaBand from "../components/CtaBand";
import NotFound from "./NotFound";
import { useProject, useServices } from "../lib/hooks";
import { categoryLabel } from "../lib/site";
import { track } from "../lib/api";

function Section({ num, title, children }) {
  return (
    <Reveal>
      <div className="grid lg:grid-cols-12 gap-6 py-12 md:py-16 border-t border-black/10">
        <div className="lg:col-span-4">
          <p className="font-mono text-xs text-ember">[ {num} ]</p>
          <h2 className="font-display font-light text-2xl md:text-3xl tracking-tight mt-3">{title}</h2>
        </div>
        <div className="lg:col-span-8 text-graphite/70 leading-relaxed text-sm md:text-base">{children}</div>
      </div>
    </Reveal>
  );
}

export default function ProjectDetail() {
  const { slug } = useParams();
  const { data: p, isLoading, isError } = useProject(slug);
  const { data: services } = useServices();
  const [lightbox, setLightbox] = useState(null); // index into p.gallery or null

  const closeLightbox = useCallback(() => setLightbox(null), []);
  useEffect(() => {
    if (lightbox === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % p.gallery.length);
      if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + p.gallery.length) % p.gallery.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, p]);

  useEffect(() => {
    if (p) track("project_view", { project: p.slug });
  }, [p]);

  if (isLoading) return <div className="min-h-screen bg-graphite" />;
  if (isError || !p) {
    const target = legacyProjectRedirect(slug);
    if (target) return <Navigate to={target} replace />;
    return <NotFound />;
  }

  const relatedServices = (services || []).filter((s) => (p.related_services || []).includes(s.slug));
  const REGION_AREA = {
    bristol: ["/areas-we-cover/builders-bristol", "our Bristol work"],
    bath: ["/areas-we-cover/builders-bath", "our Bath work"],
    "weston-super-mare": ["/areas-we-cover/builders-weston-super-mare", "our Weston-super-Mare work"],
    somerset: ["/areas-we-cover/builders-somerset", "our Somerset work"],
    bridgwater: ["/areas-we-cover/builders-bridgwater", "our Bridgwater work"],
    taunton: ["/areas-we-cover/builders-taunton", "our Taunton work"],
    newport: ["/areas-we-cover/builders-newport", "our Newport work"],
    "south-wales": ["/areas-we-cover/builders-newport", "our South Wales work"],
    gwent: ["/areas-we-cover/builders-newport", "our South Wales work"],
    devon: ["/areas-we-cover/builders-devon", "our Devon work"],
    exeter: ["/areas-we-cover/builders-exeter", "our Exeter work"],
    plymouth: ["/areas-we-cover/builders-devon", "our Devon work"],
    torbay: ["/areas-we-cover/builders-devon", "our Devon work"],
  };
  const areaLink = REGION_AREA[p.region];

  return (
    <div data-testid="project-detail-page">
      <Seo
        title={p.seo_title || `${p.title} — ${p.subtitle} | NOOR Design & Build`}
        description={p.seo_description || `${p.subtitle} in ${p.location} by NOOR Design & Build.`}
        path={`/projects/${p.slug}`}
        image={p.cover}
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              headline: `${p.title} — ${p.subtitle}`,
              description: p.summary,
              image: `https://www.noordb.co.uk${p.cover}`,
              datePublished: p.updated_at,
              dateModified: p.updated_at,
              contentLocation: p.location,
              author: { "@type": "Organization", name: "NOOR Design & Build Ltd", url: "https://www.noordb.co.uk" },
              publisher: { "@type": "Organization", name: "NOOR Design & Build Ltd", url: "https://www.noordb.co.uk" },
              mainEntityOfPage: `https://www.noordb.co.uk/projects/${p.slug}`,
            },
            breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Projects", path: "/projects" }, { name: p.title, path: `/projects/${p.slug}` }]),
          ],
        }}
      />

      <section className="bg-graphite text-white">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 pt-32 md:pt-44 pb-10 md:pb-14">
          <nav className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40" aria-label="Breadcrumb" data-testid="project-breadcrumb">
            <Link to="/" className="hover:text-ember">Home</Link> / <Link to="/projects" className="hover:text-ember">Projects</Link> / <span className="text-ember">{p.title}</span>
          </nav>
          <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-6 leading-[1.05]">
            {p.subtitle ? `${p.subtitle} in ${p.location}` : p.title}
          </h1>
          <p className="text-ember font-mono text-xs tracking-[0.25em] uppercase mt-4">{p.title}</p>
          <div className="flex flex-wrap gap-x-10 gap-y-3 mt-8 font-mono text-[10px] tracking-[0.2em] uppercase text-white/50">
            <span>Location — {p.location}</span>
            <span>Type — {(p.categories || []).map(categoryLabel).join(", ")}</span>
          </div>
          <p data-testid="project-photo-credit" className="mt-3 font-mono text-[10px] tracking-[0.2em] uppercase text-white/35">
            Photography — Guri Photography
          </p>
          <div className="mt-8">
            <Link
              to="/request-a-consultation"
              data-testid="project-consultation-button"
              className="group inline-flex items-center justify-center gap-2.5 bg-ember hover:bg-emberdark text-white px-6 py-3.5 text-sm font-semibold tracking-wide transition-colors duration-300"
            >
              Request a Consultation <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
          <dl className="mt-10 grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10 max-w-3xl" data-testid="project-details-box">
            {[
              ["Project type", (p.categories || []).map(categoryLabel).join(", ")],
              ["Location", p.location],
              ["Scope", p.subtitle],
            ].map(([k, v]) => (
              <div key={k} className="bg-graphite p-5">
                <dt className="font-mono text-[9px] tracking-[0.25em] uppercase text-white/40">{k}</dt>
                <dd className="text-sm text-white/85 mt-2 leading-snug">{v}</dd>
              </div>
            ))}
          </dl>
          {p.placeholder && (
            <p data-testid="project-placeholder-note" className="mt-8 inline-block border border-white/20 text-white/50 font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-2.5">
              Design concept — imagery illustrative of the standard and style we deliver
            </p>
          )}
        </div>
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 pb-16 md:pb-24">
          <div className="border border-white/15 p-2 md:p-3">
            <FrameReveal src={p.cover} alt={`${p.title} — ${p.subtitle}, ${p.location}`} className="aspect-[16/8]" hoverZoom={false} eager testid="project-hero-image" />
          </div>
        </div>
      </section>

      <section className="bg-cream">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 pb-16 md:pb-24">
          <Section num="01" title="Overview"><p>{p.summary}</p></Section>
          <Section num="02" title="Scope of Work"><p>{p.scope}</p></Section>
          <Section num="03" title="Client Objective"><p>{p.objective}</p></Section>
          <Section num="04" title="Challenges"><p>{p.challenges}</p></Section>
          <Section num="05" title="NOOR's Solution"><p>{p.solution}</p></Section>
          {p.features?.length > 0 && (
            <Section num="06" title="Key Features">
              <ul className="space-y-3">
                {p.features.map((f, i) => (
                  <li key={i} className="flex gap-4">
                    <span className="font-mono text-[10px] text-ember mt-1">{String(i + 1).padStart(2, "0")}</span>{f}
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </div>
      </section>

      {p.before_after?.length > 0 && (
        <section className="bg-cream border-t border-black/10" data-testid="before-after-section">
          <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-16 md:py-24">
            <Reveal>
              <Overline>[ Before &amp; After ]</Overline>
              <p className="text-sm text-graphite/55 mt-4 max-w-xl">Drag the handle to see the transformation.</p>
            </Reveal>
            <div className="grid sm:grid-cols-2 gap-6 mt-10">
              {p.before_after.map((pair, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <BeforeAfter
                    before={pair.before}
                    after={pair.after}
                    alt={`${p.title} ${pair.label || ""}`}
                    testid={`before-after-${i}`}
                  />
                  {pair.label && (
                    <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-graphite/45 mt-3">{pair.label}</p>
                  )}
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {p.gallery?.length > 0 && (
        <section className="bg-cream border-t border-black/10">
          <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-16 md:py-24">
            <Reveal><Overline>[ Project Gallery ]</Overline></Reveal>
            <div className="grid sm:grid-cols-2 gap-6 mt-10">
              {p.gallery.map((g, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <button
                    type="button"
                    onClick={() => setLightbox(i)}
                    data-testid={`gallery-open-${i}`}
                    aria-label={`Enlarge gallery image ${i + 1} of ${p.gallery.length}`}
                    className="block w-full border border-black/10 p-2 cursor-zoom-in"
                  >
                    <FrameReveal src={g} alt={`${p.title} project gallery image ${i + 1}`} className="aspect-[4/3]" testid={`gallery-image-${i}`} />
                  </button>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {p.testimonial && (
        <section className="bg-stone">
          <div className="max-w-[900px] mx-auto px-5 md:px-10 py-16 md:py-24">
            <Reveal>
              <div className="flex items-center gap-1 mb-6" data-testid="project-testimonial-stars" aria-label="Five star review">
                {[0, 1, 2, 3, 4].map((s) => (
                  <Star key={s} size={16} className="text-[#D4A017]" fill="#D4A017" strokeWidth={0} />
                ))}
              </div>
              <blockquote className="serif-accent text-graphite/85 text-2xl md:text-3xl leading-snug">“{p.testimonial.text}”</blockquote>
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-graphite/50 mt-6">
                {p.testimonial.name} · {p.testimonial.location}
              </p>
            </Reveal>
          </div>
        </section>
      )}

      {(relatedServices.length > 0 || areaLink) && (
        <section className="bg-charcoal text-white">
          <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-16 md:py-24">
            <Reveal><Overline light>[ Related Services &amp; Areas ]</Overline></Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-10">
              {relatedServices.map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`project-related-service-${s.slug}`} className="group bg-charcoal hover:bg-graphite p-8 transition-colors duration-300">
                  <p className="font-display font-medium text-lg group-hover:text-ember transition-colors duration-300">{s.title}</p>
                  <p className="text-sm text-white/50 mt-2 leading-relaxed">{s.summary}</p>
                  <span className="inline-flex items-center gap-2 mt-4 text-[10px] font-mono tracking-[0.2em] uppercase text-white/40">View <ArrowUpRight size={12} /></span>
                </Link>
              ))}
              {areaLink && (
                <Link to={areaLink[0]} data-testid="project-area-link" className="group bg-charcoal hover:bg-graphite p-8 transition-colors duration-300">
                  <p className="font-display font-medium text-lg group-hover:text-ember transition-colors duration-300">More of {areaLink[1]}</p>
                  <p className="text-sm text-white/50 mt-2 leading-relaxed">See how NOOR works with homeowners in {p.location} and the surrounding area.</p>
                  <span className="inline-flex items-center gap-2 mt-4 text-[10px] font-mono tracking-[0.2em] uppercase text-white/40">View <ArrowUpRight size={12} /></span>
                </Link>
              )}
            </div>
          </div>
        </section>
      )}

      <RelatedInsights
        title="Related Reading"
        keywords={[
          ...(p.categories || []).map(categoryLabel),
          p.location,
          p.region,
        ].filter(Boolean)}
      />

      <CtaBand title="Planning something similar for your home?" />

      <AnimatePresence>
        {lightbox !== null && p.gallery?.[lightbox] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[80] bg-graphite/95 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
            data-testid="gallery-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={`${p.title} gallery image ${lightbox + 1} of ${p.gallery.length}`}
            onClick={closeLightbox}
          >
            <button
              type="button"
              onClick={closeLightbox}
              data-testid="gallery-close"
              aria-label="Close gallery"
              className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 flex items-center justify-center text-white/80 hover:text-ember border border-white/20 hover:border-ember transition-colors duration-300"
            >
              <X size={20} strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + p.gallery.length) % p.gallery.length); }}
              data-testid="gallery-prev"
              aria-label="Previous image"
              className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center text-white/80 hover:text-ember border border-white/20 hover:border-ember transition-colors duration-300"
            >
              <ChevronLeft size={22} strokeWidth={1.5} />
            </button>
            <motion.img
              key={lightbox}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              src={p.gallery[lightbox]}
              alt={`${p.title} project gallery image ${lightbox + 1}`}
              data-testid="gallery-lightbox-image"
              className="max-w-full max-h-[82vh] object-contain border border-white/15"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % p.gallery.length); }}
              data-testid="gallery-next"
              aria-label="Next image"
              className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center text-white/80 hover:text-ember border border-white/20 hover:border-ember transition-colors duration-300"
            >
              <ChevronRight size={22} strokeWidth={1.5} />
            </button>
            <p className="absolute bottom-4 left-1/2 -translate-x-1/2 font-mono text-[10px] tracking-[0.25em] uppercase text-white/50" data-testid="gallery-counter">
              {lightbox + 1} / {p.gallery.length}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
